import { Users2, Sparkles, ArrowUpRight, Star, ExternalLink } from "lucide-react";
import { ASSETS } from "../lib/constants";
import { useContent, resolveImage } from "../contexts/ContentContext";

const PartnerShell = ({ url, className, children, testid }) => {
  if (url) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" data-testid={testid} className={className}>
        {children}
      </a>
    );
  }
  return (
    <div data-testid={testid} className={className}>
      {children}
    </div>
  );
};

export const Partners = () => {
  const { data: partners } = useContent("partners");
  if (!partners) return null;

  const items = partners.items || [];
  const featured = items.filter((p) => p.featured);
  const others = items.filter((p) => !p.featured);

  return (
    <section id="partners" data-testid="partners-section" className="py-20 md:py-28 bg-white relative overflow-hidden">
      <div aria-hidden="true" className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[400px] bg-[#FFD100]/18 blur-[120px] rounded-full" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <span className="section-eyebrow">
              <Users2 className="w-3.5 h-3.5" />
              {partners.eyebrow}
            </span>
            <h2 data-testid="partners-heading" className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900 leading-tight">
              {partners.heading}
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">{partners.intro}</p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-[28px] overflow-hidden aspect-[4/3] shadow-[0_20px_60px_rgba(15,23,42,0.15)] ring-1 ring-slate-100">
              <img src={resolveImage(partners.feature_image_url) || ASSETS.action.michiganInfluence} alt="Semaj J. Morgan meeting with Michigan community leaders" className="w-full h-full object-cover" style={{ objectPosition: "center top" }} loading="lazy" data-testid="partners-feature-image" />
              <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-[#1E3A8A]/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 bg-[#FFD100] text-[#0F172A] text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                  <Sparkles className="w-3 h-3" /> Michigan Influence
                </span>
              </div>
            </div>
          </div>
        </div>

        {featured.length > 0 && (
          <div className="mt-14">
            <div className="flex items-center gap-2 mb-5">
              <Star className="w-4 h-4 text-[#FFD100]" fill="currentColor" strokeWidth={1.5} />
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#1D4ED8]">Main Program Partners</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-5" data-testid="featured-partners-grid">
              {featured.map((p, i) => (
                <PartnerShell key={i} url={p.url} testid={`featured-partner-${i}`} className="card-soft p-8 md:p-10 flex flex-col items-center text-center group relative hover:-translate-y-1 transition-transform">
                  <span className="absolute top-5 right-5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#FFD100] bg-[#1E3A8A]/95 px-2.5 py-1 rounded-full">
                    <Star className="w-3 h-3" fill="currentColor" />
                    Featured
                  </span>
                  {p.url && (
                    <span className="absolute top-5 left-5 text-slate-400 group-hover:text-[#1D4ED8] transition-colors">
                      <ExternalLink className="w-4 h-4" />
                    </span>
                  )}
                  <div className="w-full aspect-[16/9] rounded-2xl bg-white grid place-items-center p-6 ring-1 ring-slate-100">
                    {p.logo ? (
                      <img src={resolveImage(p.logo)} alt={`${p.name} logo`} className="max-h-32 max-w-full object-contain" loading="lazy" />
                    ) : (
                      <span className="font-display text-3xl font-extrabold text-[#2563EB]">{p.name}</span>
                    )}
                  </div>
                  <p className="mt-5 font-display text-xl md:text-2xl font-bold text-slate-900">{p.name}</p>
                  <p className="mt-1.5 text-xs uppercase tracking-[0.16em] font-semibold text-[#1D4ED8]">{p.tag}</p>
                </PartnerShell>
              ))}
            </div>
          </div>
        )}

        {others.length > 0 && (
          <div className="mt-12">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-5">Location & Community Partners</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="partners-grid">
              {others.map((p, i) => (
                <PartnerShell key={i} url={p.url} testid={`partner-${i}`} className="card-soft p-5 text-center group flex flex-col items-center hover:-translate-y-1 transition-transform relative">
                  {p.url && (
                    <span className="absolute top-3 right-3 text-slate-300 group-hover:text-[#1D4ED8] transition-colors">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </span>
                  )}
                  {p.logo ? (
                    <div className="w-full aspect-square rounded-xl bg-white grid place-items-center p-3 ring-1 ring-slate-100">
                      <img src={resolveImage(p.logo)} alt={`${p.name} logo`} className="w-full h-full object-contain" loading="lazy" />
                    </div>
                  ) : (
                    <span className="inline-flex w-14 h-14 mt-2 rounded-2xl bg-[#2563EB]/10 text-[#2563EB] items-center justify-center mx-auto font-display text-base font-extrabold">
                      {p.name.split(/\s+/).slice(0, 2).map((w) => w[0]).join("")}
                    </span>
                  )}
                  <p className="mt-3 font-display text-sm font-bold text-slate-900 leading-tight">{p.name}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">{p.tag}</p>
                </PartnerShell>
              ))}
            </div>
          </div>
        )}

        <div data-testid="partners-cta" className="mt-14 rounded-[28px] bg-[#2563EB] text-white p-8 md:p-12 grid md:grid-cols-12 gap-6 items-center relative overflow-hidden">
          <div aria-hidden="true" className="absolute -bottom-12 -right-12 w-64 h-64 rounded-full bg-[#FFD100]/30 blur-3xl" />
          <div className="md:col-span-8 relative">
            <p className="text-[#FFD100] text-xs font-bold uppercase tracking-[0.2em]">Partner with us</p>
            <h3 className="mt-3 font-display text-2xl md:text-4xl font-bold leading-tight">Let's build the next generation together.</h3>
          </div>
          <div className="md:col-span-4 relative flex md:justify-end">
            <a href="#get-involved" data-testid="partners-cta-button" className="inline-flex items-center gap-2 bg-[#FFD100] hover:bg-[#FFDB33] text-[#0F172A] font-bold px-6 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(255,209,0,0.35)]">
              Become a partner <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
